import {
  Badge,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Pagination,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {useMemo, useState} from 'react';
import styles from './allRewardSchemes.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {
  GET_ALL_REWARD_SCHEMES,
  GET_USER_REWARDS,
} from 'graphql/queries/rewardScheme';
import DBModal from 'components/modal/modal';
import UserLink from 'components/userLink/UserLink';
import {UPDATE_USER_REWARD_STATUS} from 'graphql/mutations/rewardScheme';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  operationTypeEnum,
  rewardStatus,
  rewardType,
  walletCurrencies,
} from 'utils/mock';
import {
  assetClassLabel,
  instrumentKeyColor,
  instrumentKeyLabel,
} from 'pages/AssetManagement/modules/instrumentKeys';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import filterIcon from 'icons/filter.svg';
import moment from 'moment';
import gamer from 'images/gamer.png';
import dotsIcon from 'images/dots.svg';
import {ReverseReward} from '../reverseReward/reverseReward';

// Every status the backend can hold. Anything not listed here used to render
// as "Approved", which hid failed and reversed rewards in plain sight.
const STATUS_TAG = {
  APPROVED: {color: 'green', label: 'Approved'},
  PENDING: {color: 'gold', label: 'Pending'},
  REJECTED: {color: 'red', label: 'Rejected'},
  FAILED: {color: 'volcano', label: 'Failed'},
  REVERSED: {color: 'purple', label: 'Reversed'},
  EXPIRED: {color: 'default', label: 'Expired'},
};
const ALL_STATUSES = Object.keys(STATUS_TAG);

const tabs = [
  ...ALL_STATUSES.map(value => ({label: STATUS_TAG[value].label, value})),
  {label: 'All', value: 'ALL'},
];

const formatMoney = (currency, value) =>
  value === null || value === undefined
    ? '--'
    : `${currency || ''} ${Number(value).toLocaleString()}`.trim();

// Filter form values -> query variables. Empty selections drop out so the
// backend sees no filter rather than an empty list.
const toFilterVariables = values => {
  const [from, to] = values.dateRange || [];
  return {
    rewardSchemeIds: values.rewardSchemeIds?.length
      ? values.rewardSchemeIds
      : undefined,
    rewardType: values.rewardType || undefined,
    eventType: values.eventType || undefined,
    currency: values.currency || undefined,
    userType: values.userType || undefined,
    entryMode: values.entryMode || undefined,
    mode: values.mode || undefined,
    startDate: from ? moment(from).startOf('day').toISOString() : undefined,
    endDate: to ? moment(to).endOf('day').toISOString() : undefined,
  };
};

const countActiveFilters = values =>
  Object.values(values).filter(value =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  ).length;

const AllUserReward = ({userId}) => {
  const navigate = useNavigate();
  const [openStatusModal, setStatusModal] = useState(false);
  const [userReward, setUserReward] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams('');
  const [activeTab, setActiveTab] = useState(
    () =>
      tabs.find(tab => tab.label === searchParams.get('tab'))?.value ||
      'APPROVED',
  );
  const [openFilterModal, setOpenFilterModal] = useState(false);
  // The applied filters, as the form submitted them. Kept in state (not sent
  // through refetch) so a tab or page change does not silently drop them.
  const [filters, setFilters] = useState({});
  const [filterForm] = Form.useForm();

  const [openReverseModal, setOpenReverseModal] = useState({
    open: false,
    id: null,
  });
  const [reverseForm] = Form.useForm();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.RewardScheme),
  );

  const {loading, refetch, data} = useQuery(GET_USER_REWARDS, {
    variables: {
      ...(activeTab === 'ALL' ? {statuses: ALL_STATUSES} : {status: activeTab}),
      ...toFilterVariables(filters),
      page: currentPage,
      limit,
      user: userId,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  // Schemes for the filter dropdown. The list is paginated, so ask for enough
  // to cover every scheme in one go.
  const {data: schemesData} = useQuery(GET_ALL_REWARD_SCHEMES, {
    variables: {page: 1, limit: 200},
    errorPolicy: 'all',
  });
  const schemeOptions = useMemo(
    () =>
      (schemesData?.getAllRewardSchemes?.data?.rewardSchemes || []).map(
        scheme => ({
          value: scheme.id,
          label: `${scheme.title}${scheme.isActive ? '' : ' (inactive)'}`,
        }),
      ),
    [schemesData],
  );

  const activeFilterCount = countActiveFilters(filters);

  const handlePageChange = page => {
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleLimitChange = size => {
    if (size === limit) return;
    setLimit(size);
    setCurrentPage(1);
  };

  const applyFilters = values => {
    setCurrentPage(1);
    setFilters(values);
    setOpenFilterModal(false);
  };

  const clearFilters = () => {
    filterForm.resetFields();
    setCurrentPage(1);
    setFilters({});
    setOpenFilterModal(false);
  };

  const [adminUpdateUserRewardStatus, {loading: loadingUpdate}] = useMutation(
    UPDATE_USER_REWARD_STATUS,
  );

  const handleSave = formValues => {
    adminUpdateUserRewardStatus({
      variables: {
        adminUpdateUserRewardStatusId: userReward,
        status: formValues?.status,
      },
    }).then(({data: {adminUpdateUserRewardStatus}}) => {
      if (adminUpdateUserRewardStatus.__typename === 'Error') {
        toast.error(adminUpdateUserRewardStatus.message);
      } else {
        setStatusModal(false);
        refetch();
        toast.success('User reward updated successfully');
      }
    });
  };

  // On a user's own page, their name stays plain rather than linking to itself.
  const linkedUserId = user => (user?.id !== userId ? user?.id : undefined);

  const rewardTypeLabel = key =>
    rewardType.find(item => item.key === key)?.name ||
    key?.replaceAll('_', ' ') ||
    '--';

  const rewardValue = reward => {
    if (!reward?.type) return '--';
    if (reward.type === 'STOCKS') {
      // A ticker alone is no longer unique — the same symbol can list on two
      // exchanges — so name the exchange when the record carries one.
      const ticker = reward.ticker
        ? `${reward.ticker}${
            reward.exchangeCode ? ` · ${reward.exchangeCode}` : ''
          }`
        : 'Any';
      return `${reward.rewardUnits ?? '--'} units of ${ticker}`;
    }
    const value =
      reward.type === 'TRANSACTION_FEE_WAIVER'
        ? reward.cashValue
        : reward.amount;
    return formatMoney(reward.currency?.currency, value);
  };

  const subject = record =>
    record?.userType === 'REFEREE'
      ? record?.refereeId
      : record?.userType === 'REFERRER'
      ? record?.referrerId
      : record?.userId;

  // Ordered by what an admin scans for first: who, under which scheme, what
  // they got and whether it landed. The triggering event and its money sit
  // together next; provenance and dates last.
  const columns = [
    {
      title: 'User',
      key: 'user',
      fixed: 'left',
      width: 210,
      render: (text, record) => {
        const user = subject(record);
        return (
          <div className={styles.userCell}>
            <img src={user?.imageUrl || gamer} alt="" />
            <div className={styles.cell}>
              <span className={styles.cellPrimary}>
                <UserLink userId={linkedUserId(user)}>
                  {user?.firstName} {user?.lastName}
                </UserLink>
              </span>
              <span className={styles.cellSecondary}>
                {record?.userType === 'USER'
                  ? 'User'
                  : record?.userType === 'REFEREE'
                  ? 'Referee'
                  : 'Referrer'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Scheme',
      key: 'rewardScheme',
      width: 230,
      render: (text, record) =>
        record?.rewardScheme?.id ? (
          <div className={styles.cell}>
            <Tooltip title={record.rewardScheme.title}>
              <Link
                className={styles.ellipsis}
                to={`/dashboard/reward-schemes/update/${record.rewardScheme.id}/EN/users`}>
                {record.rewardScheme.title}
              </Link>
            </Tooltip>
            <span className={styles.cellSecondary}>
              {record.rewardScheme.type?.replaceAll('_', ' ')}
            </span>
          </div>
        ) : (
          '--'
        ),
    },
    {
      title: 'Reward',
      key: 'reward',
      width: 180,
      render: (text, record) => (
        <div className={styles.cell}>
          <span className={styles.cellPrimary}>
            {rewardValue(record?.userReward)}
          </span>
          <span className={styles.cellSecondary}>
            {rewardTypeLabel(record?.userReward?.type)}
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (text, record) => {
        const tag = STATUS_TAG[record?.status] || {
          color: 'default',
          label: record?.status || '--',
        };
        const reversal = record?.reversalRecord;
        return (
          <div className={styles.cellTags}>
            <Tag color={tag.color}>{tag.label}</Tag>
            {reversal?.status === 'REVERSED' && (
              <Tooltip title={reversal.reversalReason}>
                <Tag color="red">Reversed</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      // What triggered the reward, at both levels of the scheme's scope — a
      // reward that paid on a listed stock vs one on an IPO allocation is only
      // visible here.
      title: 'Event',
      key: 'event',
      width: 180,
      render: (text, record) => (
        <div className={styles.cell}>
          <span className={styles.cellPrimary}>
            {record?.eventType?.replaceAll('_', ' ') || '--'}
          </span>
          {(record?.eventAssetClassKey || record?.eventInstrumentKey) && (
            <div className={styles.cellTags}>
              {record?.eventAssetClassKey && (
                <span className={styles.cellSecondary}>
                  {assetClassLabel(record.eventAssetClassKey)}
                </span>
              )}
              {record?.eventInstrumentKey && (
                <Tag color={instrumentKeyColor(record.eventInstrumentKey)}>
                  {instrumentKeyLabel(record.eventInstrumentKey)}
                </Tag>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'eventAmount',
      width: 150,
      render: (text, record) => (
        <span className={styles.cellPrimary}>
          {formatMoney(record?.eventCurrency?.currency, record?.eventAmount)}
        </span>
      ),
    },
    {
      // Kept as its own column: for a fee waiver this is the base the reward
      // was computed from, so the cap is visible next to the payout.
      title: 'Fees',
      key: 'eventFees',
      width: 130,
      render: (text, record) => (
        <span className={styles.cellPrimary}>
          {formatMoney(record?.eventCurrency?.currency, record?.eventFees)}
        </span>
      ),
    },
    {
      title: 'Date',
      key: 'createdAt',
      width: 140,
      render: (text, record) => (
        <div className={styles.cell}>
          <span className={styles.cellPrimary}>
            {moment(record?.createdAt).format('DD MMM YYYY')}
          </span>
          {record?.updatedAt &&
            !moment(record.updatedAt).isSame(record.createdAt, 'day') && (
              <span className={styles.cellSecondary}>
                Updated {moment(record.updatedAt).format('DD MMM YYYY')}
              </span>
            )}
        </div>
      ),
    },
    {
      title: 'Source',
      key: 'source',
      width: 130,
      render: (text, record) => (
        <div className={styles.cellTags}>
          {record?.entryMode === 'MANUAL' ? (
            <Tag color="default">Manual</Tag>
          ) : (
            <Tag color="blue">Automatic</Tag>
          )}
          {record?.mode && record.mode !== 'LIVE' && (
            <Tag color="orange">Simulation</Tag>
          )}
        </div>
      ),
    },
  ];

  if (tabOperations?.update) {
    columns.push({
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 56,
      render: (text, record) => (
        <div className={styles.actions}>
          {['PENDING', 'APPROVED'].includes(record?.status) && (
            <Dropdown
              trigger="click"
              menu={{
                items: [
                  {
                    key: 1,
                    label: 'Update Status',
                    disabled: record?.status === 'APPROVED',
                    onClick: () => {
                      setStatusModal(true);
                      setUserReward(record?.id);
                    },
                  },
                  {
                    key: 2,
                    label: 'Update Reward',
                    disabled:
                      record?.entryMode === 'AUTOMATIC' ||
                      record?.status === 'APPROVED',
                    onClick: () =>
                      navigate(
                        '/dashboard/reward-schemes/update-user-reward/' +
                          record?.id,
                      ),
                  },
                  {
                    key: 3,
                    label: 'Reverse Reward',
                    disabled:
                      record?.status === 'PENDING' ||
                      record?.reversalRecord?.status === 'REVERSED',
                    onClick: () => {
                      setOpenReverseModal({open: true, id: record?.id});
                    },
                  },
                ],
              }}>
              <div>
                <img src={dotsIcon} alt="" />
              </div>
            </Dropdown>
          )}
        </div>
      ),
    });
  }

  return (
    <div className={styles['table-container']}>
      <div className={styles['details-toggler']}>
        {tabs.map(tab => (
          <span
            className={activeTab === tab.value ? styles.active : ''}
            onClick={() => {
              setCurrentPage(1);
              setActiveTab(tab.value);
              setSearchParams({tab: tab.label});
            }}
            key={tab.value}>
            {tab.label}
          </span>
        ))}
      </div>
      <div className={styles.filterSection}>
        <Badge count={activeFilterCount} size="small" offset={[-6, 6]}>
          <div
            onClick={() => setOpenFilterModal(!openFilterModal)}
            className={styles.filter}>
            <h2>Filter</h2>
            <img src={filterIcon} alt="" />
          </div>
        </Badge>
        <div
          style={{display: openFilterModal ? 'block' : 'none'}}
          className={styles.filterItems}>
          <h3>FILTER REWARDS</h3>
          <Form form={filterForm} onFinish={applyFilters} layout="vertical">
            <div className={styles.filterGrid}>
              <Form.Item
                name="rewardSchemeIds"
                label="Reward Scheme"
                className={styles.filterWide}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  placeholder="Any scheme"
                  options={schemeOptions}
                />
              </Form.Item>
              <Form.Item
                name="dateRange"
                label="Granted Between"
                className={styles.filterWide}>
                <DatePicker.RangePicker
                  style={{width: '100%'}}
                  format="DD MMM YYYY"
                />
              </Form.Item>
              <Form.Item name="rewardType" label="Reward Type">
                <Select allowClear placeholder="Any">
                  {rewardType.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="eventType" label="Action Type">
                <Select allowClear placeholder="Any">
                  {operationTypeEnum.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="currency" label="Event Currency">
                <Select allowClear showSearch placeholder="Any">
                  {walletCurrencies.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {key} · {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="userType" label="User Type">
                <Select allowClear placeholder="Any">
                  <Select.Option value="REFERRER">Referrer</Select.Option>
                  <Select.Option value="REFEREE">Referee</Select.Option>
                  <Select.Option value="USER">User</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="entryMode" label="Entry Mode">
                <Select allowClear placeholder="Any">
                  <Select.Option value="MANUAL">Manual</Select.Option>
                  <Select.Option value="AUTOMATIC">Automatic</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="mode" label="Mode">
                <Select allowClear placeholder="Any">
                  <Select.Option value="LIVE">Live</Select.Option>
                  <Select.Option value="SIMULATION">Simulation</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div className={styles.filterActions}>
              <SubmitButton label="Filter" />
              <Button type="link" htmlType="button" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </Form>
        </div>
      </div>
      <div className={styles['startup-table']}>
        <>
          <Table
            className={styles['user-table']}
            dataSource={data?.adminGetAllUserRewards?.data}
            columns={columns}
            loading={loading}
            pagination={false}
            rowKey="id"
            scroll={{x: 1550}}
          />
          <Pagination
            current={currentPage}
            onChange={e => handlePageChange(e)}
            total={data?.adminGetAllUserRewards?.pagination?.total}
            showQuickJumper
            className="ant-table-pagination"
            showSizeChanger
            pageSize={limit}
            onShowSizeChange={(_, size) => {
              handleLimitChange(size);
            }}
          />
        </>
      </div>

      <DBModal
        isOpen={openStatusModal}
        handleClose={() => setStatusModal(false)}
        content={
          <div>
            <h1>Update Status</h1>
            <Form
              className={styles.form}
              layout="vertical"
              onFinish={handleSave}>
              <div className={styles['date-format']}>
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Status required',
                    },
                  ]}
                  name="status"
                  label="Status">
                  <Select style={{width: 340}} name="status">
                    {rewardStatus
                      .filter(item => item.key !== 'PENDING')
                      .map(({key, name}) => (
                        <Select.Option value={key} key={key}>
                          {name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
              <SubmitButton disabled={loadingUpdate} label="Update" />
            </Form>
          </div>
        }
      />

      <DBModal
        isOpen={openReverseModal.open}
        handleClose={() => setOpenReverseModal({open: false, id: null})}
        content={
          <ReverseReward
            onClose={() => {
              setOpenReverseModal({open: false, id: null});
              reverseForm.resetFields();
            }}
            id={openReverseModal.id}
            form={reverseForm}
            refetch={refetch}
          />
        }
      />
    </div>
  );
};

export {AllUserReward};
